import {
  deleteLibraryItem,
  listLibraryItems,
  uploadLibraryFile,
} from './sharepoint';

const LIST = 'supplierDocuments';

/**
 * Per-supplier file storage (contracts, certificates of insurance, W-9s,
 * quality docs, etc.). Files live in the SupplierDocuments SharePoint
 * document library — same pattern as Sales Resources — but each upload is
 * tagged with the SupplierCode it belongs to, so the Supplier detail page
 * can show just that supplier's documents.
 *
 * Each returned row has: id, driveItemId, fileName, sizeBytes, downloadUrl,
 * webUrl, plus the library columns Title, SupplierCode, Description,
 * Category, PostedBy, PostedAt.
 */

/**
 * List the documents for one supplier, newest upload first. We pull the
 * library and filter by SupplierCode in memory — the libraries here are
 * small and this avoids relying on an indexed column for filtering.
 */
export async function listSupplierDocuments(supplierCode) {
  const sc = (supplierCode || '').trim().toUpperCase();
  if (!sc) return [];
  const rows = await listLibraryItems(LIST);
  return rows
    .filter((r) => (r.SupplierCode || '').trim().toUpperCase() === sc)
    .sort((a, b) => {
      const ta = Date.parse(a.PostedAt || a.Modified || a.Created || '') || 0;
      const tb = Date.parse(b.PostedAt || b.Modified || b.Created || '') || 0;
      return tb - ta;
    });
}

/**
 * Upload a document for a supplier. `file` is a browser File; the rest are
 * stored as library columns alongside it. Title defaults to the file's base
 * name when not supplied.
 */
export async function createSupplierDocument({
  file,
  supplierCode,
  Title,
  Description = '',
  Category = '',
  PostedBy = '',
  PostedAt,
}) {
  if (!file) throw new Error('createSupplierDocument: file is required.');
  if (!supplierCode) {
    throw new Error('createSupplierDocument: supplierCode is required.');
  }
  const fallbackTitle = (file.name || '').replace(/\.[^.]+$/, '');
  const fields = {
    Title: (Title || fallbackTitle || 'Untitled').trim(),
    SupplierCode: String(supplierCode).trim(),
    Description,
    Category,
    PostedBy,
    PostedAt: PostedAt || new Date().toISOString(),
  };
  return uploadLibraryFile(LIST, file, fields);
}

export const deleteSupplierDocument = (id) => deleteLibraryItem(LIST, id);
